import { coreCall } from './gatewayService';
import { useSettingsStore } from '../stores/settingsStore';
import { customerOrderSettings } from '../utils/customerOrderSettings';
/**
 * Service for managing manual orders (CRM-created orders)
 * Handles CRUD operations, stock reservations, and order lifecycle
 */

import { supabase } from '../supabase';
import { StockReservationService } from './stockReservationService';
import { useOrderStatusHistoryStore } from '../stores/orderStatusHistoryStore';
import { refreshOrderFromDeal as refreshOrderFromDealCore } from './dealOrderService';
import { CUSTOM_LINE_PRODUCT_SLUG, CUSTOM_LINE_PRODUCT_NAME } from '../orders/customOrderLine';
import { withoutRemovedStoreLines } from '../orders/storeOrderLines';
import type {
  ManualOrder,
  OrderItem,
  CreateManualOrderRequest,
  UpdateManualOrderRequest,
  ManualOrderStatus,
  ProductAvailability,
  OrderItemInput
} from '../types/orders';

interface AuditContext {
  organizationId: string;
  userId: string;
}

export class ManualOrderService {

  // ========================================
  // Order CRUD Operations
  // ========================================

  /**
   * Generate a new order number
   */
  static async generateOrderNumber(organizationId: string): Promise<string> {
    const { data, error } = await supabase.rpc('generate_order_number', {
      p_org_id: organizationId
    });

    if (error) {
      console.error('Error generating order number:', error);
      // Fallback: generate manually
      const year = new Date().getFullYear();
      const timestamp = Date.now().toString().slice(-5);
      return `ORD-${year}-${timestamp}`;
    }

    return data;
  }

  /**
   * Create a new manual order
   */
  static async createOrder(
    request: CreateManualOrderRequest,
    context: AuditContext
  ): Promise<ManualOrder> {
    if (customerOrderSettings(useSettingsStore.getState().settings).enabled) {
      const { data, error } = await coreCall<{ order: ManualOrder }>('orders/manual', { body: { ...request, request_id: request.request_id || crypto.randomUUID() } });
      if (error || !data?.order) throw new Error(error?.message || 'Creazione ordine non riuscita');
      return data.order;
    }
    // Generate order number
    const orderNumber = await this.generateOrderNumber(context.organizationId);

    // Calculate totals
    const subtotal = request.items.reduce((sum, item) => {
      const lineTotal = (item.unit_price * item.quantity) - (item.discount_amount || 0);
      return sum + lineTotal;
    }, 0);

    const discountTotal = request.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
    const taxTotal = request.items.reduce((sum, item) => {
      const lineTotal = (item.unit_price * item.quantity) - (item.discount_amount || 0);
      return sum + (lineTotal * (item.tax_rate || 0) / 100);
    }, 0);
    const total = subtotal + (request.shipping_cost || 0) + taxTotal;

    // Create order
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .insert({
        organization_id: context.organizationId,
        order_number: orderNumber,
        status: request.auto_confirm ? 'confirmed' : 'draft',
        client_id: request.client_id,
        deal_id: request.deal_id,
        price_list_id: request.price_list_id,
        subtotal,
        discount_amount: discountTotal,
        shipping_cost: request.shipping_cost || 0,
        tax_amount: taxTotal,
        total,
        currency: 'EUR',
        billing_address: request.billing_address,
        shipping_address: request.shipping_address,
        shipping_location_id: request.shipping_location_id || null,
        shipping_method: request.shipping_method,
        customer_notes: request.customer_notes,
        internal_notes: request.internal_notes,
        created_by: context.userId,
        confirmed_at: request.auto_confirm ? new Date().toISOString() : null
      })
      .select()
      .single();

    if (orderError) {
      console.error('Error creating order:', orderError);
      throw new Error(`Failed to create order: ${orderError.message}`);
    }

    // Create order items
    const orderItems = request.items.map((item, index) => ({
      order_id: order.id,
      product_id: item.product_id,
      variation_id: item.variation_id,
      product_name: item.product_name,
      product_sku: item.product_sku,
      sale_unit: item.sale_unit ?? null,
      pack_weight_kg: item.pack_weight_kg ?? null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_amount: item.discount_amount || 0,
      line_total: (item.unit_price * item.quantity) - (item.discount_amount || 0),
      tax_rate: item.tax_rate || 0,
      sort_order: index,
      notes: item.notes,
      line_type: item.line_type ?? 'product'
    }));

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems);

    if (itemsError) {
      // Rollback: delete the order
      await supabase.from('orders').delete().eq('id', order.id);
      console.error('Error creating order items:', itemsError);
      throw new Error(`Failed to create order items: ${itemsError.message}`);
    }

    // If auto_confirm, create stock reservations
    if (request.auto_confirm) {
      try {
        await this.createStockReservations(order.id, context);
      } catch (reservationError) {
        console.error('Warning: Stock reservation failed:', reservationError);
        // Non bloccare la creazione dell'ordine, ma logga il warning
      }
    }

    // Fetch complete order with relations
    return this.getOrderById(order.id, context.organizationId);
  }

  /**
   * Prodotto segnaposto delle righe libere (prodotto non a catalogo).
   * E' lo stesso che usa flusia-core per le righe senza prodotto dei negozi:
   * inattivo, senza giacenza, escluso dalla sincronizzazione negozi. Se l'azienda
   * non lo ha ancora, lo crea (un solo prodotto per azienda: indice unico su slug).
   */
  static async getCustomLineProductId(organizationId: string): Promise<string> {
    const find = async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('slug', CUSTOM_LINE_PRODUCT_SLUG)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.id as string | undefined;
    };
    const existing = await find();
    if (existing) return existing;
    const { error: insertError } = await supabase
      .from('products')
      .upsert({
        organization_id: organizationId,
        name: CUSTOM_LINE_PRODUCT_NAME,
        slug: CUSTOM_LINE_PRODUCT_SLUG,
        status: 'inactive',
        type: 'simple',
        manage_stock: false,
        is_sellable: false,
        is_visible_on_website: false,
        stock_quantity: 0
      }, { onConflict: 'organization_id,slug', ignoreDuplicates: true });
    if (insertError) throw new Error(`Impossibile preparare la riga libera: ${insertError.message}`);
    const created = await find();
    if (!created) throw new Error('Impossibile preparare la riga libera');
    return created;
  }

  /**
   * Get order by ID with all relations
   */
  static async getOrderById(orderId: string, organizationId: string): Promise<ManualOrder> {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        client:clients(id, business_name, email, phone, vat_number, stato_cliente),
        deal:deals(id, title),
        items:order_items(
          *,
          product:products(id, name, sku, stock_quantity, unit_of_measure_1)
        ),
        created_by_user:user_profiles!created_by(id, full_name, email)
      `)
      .eq('id', orderId)
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      console.error('Error fetching order:', error);
      throw new Error(`Order not found: ${error.message}`);
    }

    // Righe tolte nel negozio (quantita' 0, tenute per lo storico): non si mostrano, come in WooCommerce
    return { ...data, items: withoutRemovedStoreLines(data.items) } as ManualOrder;
  }

  /**
   * Get orders with filters
   */
  static async getOrders(
    organizationId: string,
    filters?: {
      status?: ManualOrderStatus;
      clientId?: string;
      dealId?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ orders: ManualOrder[]; total: number }> {
    let query = supabase
      .from('orders')
      .select(`
        *,
        client:clients(id, business_name, email),
        items:order_items(id)
      `, { count: 'exact' })
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.clientId) {
      query = query.eq('client_id', filters.clientId);
    }

    if (filters?.dealId) {
      query = query.eq('deal_id', filters.dealId);
    }

    if (filters?.search) {
      query = query.or(
        `order_number.ilike.%${filters.search}%`
      );
    }

    if (filters?.dateFrom) {
      query = query.gte('order_date', filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte('order_date', filters.dateTo);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching orders:', error);
      throw new Error(`Failed to fetch orders: ${error.message}`);
    }

    return {
      orders: (data || []) as ManualOrder[],
      total: count || 0
    };
  }

  /**
   * Update an order
   */
  static async updateOrder(
    orderId: string,
    updates: UpdateManualOrderRequest,
    context: AuditContext
  ): Promise<ManualOrder> {
    const { data, error } = await supabase
      .from('orders')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('organization_id', context.organizationId)
      .select()
      .single();

    if (error) {
      console.error('Error updating order:', error);
      throw new Error(`Failed to update order: ${error.message}`);
    }

    return this.getOrderById(orderId, context.organizationId);
  }

  /**
   * «Aggiorna dall'offerta» su un ordine in bozza: riallinea all'offerta SOLO
   * le righe che appartengono a questo ordine (un'offerta può essere divisa in
   * più ordini). Regola in flusia-core orders/repository.ts refreshOrderFromDeal:
   * prezzo, nome, SKU, IVA e UM dall'offerta; la quantità segue l'offerta se
   * l'ordine è l'unico ad avere la riga, altrimenti scende solo se l'offerta ne
   * prevede meno; righe nuove dell'offerta restano da assegnare. Spedizione,
   * indirizzi e note dell'ordine restano com'erano.
   */
  static async refreshOrderFromDeal(orderId: string, context: AuditContext): Promise<ManualOrder> {
    await refreshOrderFromDealCore(orderId, context.organizationId);
    return this.getOrderById(orderId, context.organizationId);
  }

  /**
   * Delete an order (only if draft)
   */
  static async deleteOrder(orderId: string, context: AuditContext): Promise<void> {
    // Check if order is in draft status
    const { data: order, error: fetchError } = await supabase
      .from('orders')
      .select('status')
      .eq('id', orderId)
      .eq('organization_id', context.organizationId)
      .single();

    if (fetchError || !order) {
      throw new Error('Order not found');
    }

    if (order.status !== 'draft') {
      throw new Error('Can only delete draft orders. Cancel the order instead.');
    }

    const { error } = await supabase
      .from('orders')
      .delete()
      .eq('id', orderId)
      .eq('organization_id', context.organizationId);

    if (error) {
      console.error('Error deleting order:', error);
      throw new Error(`Failed to delete order: ${error.message}`);
    }
  }

  /**
   * Update order items (only for draft orders)
   * Replaces all items, recalculates totals
   */
  static async updateOrderItems(
    orderId: string,
    items: OrderItemInput[],
    context: AuditContext
  ): Promise<ManualOrder> {
    // Verify order is draft
    const { data: order, error: fetchError } = await supabase
      .from('orders')
      .select('status')
      .eq('id', orderId)
      .eq('organization_id', context.organizationId)
      .single();

    if (fetchError || !order) {
      throw new Error('Order not found');
    }

    if (order.status !== 'draft') {
      throw new Error('Can only edit items on draft orders');
    }

    // Delete existing items
    const { error: deleteError } = await supabase
      .from('order_items')
      .delete()
      .eq('order_id', orderId);

    if (deleteError) {
      throw new Error(`Failed to delete existing items: ${deleteError.message}`);
    }

    // Insert new items
    const orderItems = items.map((item, index) => ({
      order_id: orderId,
      product_id: item.product_id,
      variation_id: item.variation_id,
      product_name: item.product_name,
      product_sku: item.product_sku,
      sale_unit: item.sale_unit ?? null,
      pack_weight_kg: item.pack_weight_kg ?? null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_amount: item.discount_amount || 0,
      line_total: (item.unit_price * item.quantity) - (item.discount_amount || 0),
      tax_rate: item.tax_rate || 0,
      sort_order: index,
      notes: item.notes,
      line_type: item.line_type ?? 'product'
    }));

    if (orderItems.length > 0) {
      const { error: insertError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (insertError) {
        throw new Error(`Failed to insert items: ${insertError.message}`);
      }
    }

    // Recalculate totals
    const subtotal = orderItems.reduce((sum, item) => sum + item.line_total, 0);
    const discountTotal = orderItems.reduce((sum, item) => sum + item.discount_amount, 0);
    const taxTotal = orderItems.reduce((sum, item) => {
      return sum + (item.line_total * item.tax_rate / 100);
    }, 0);

    const { data: currentOrder } = await supabase
      .from('orders')
      .select('shipping_cost')
      .eq('id', orderId)
      .single();

    const total = subtotal + (currentOrder?.shipping_cost || 0) + taxTotal;

    const { error: updateError } = await supabase
      .from('orders')
      .update({
        subtotal,
        discount_amount: discountTotal,
        tax_amount: taxTotal,
        total,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('organization_id', context.organizationId);

    if (updateError) {
      throw new Error(`Failed to update order totals: ${updateError.message}`);
    }

    return this.getOrderById(orderId, context.organizationId);
  }

  // ========================================
  // Order Status Management
  // ========================================

  /**
   * Confirm an order (from draft to confirmed)
   * Creates stock reservations for all items
   */
  static async confirmOrder(orderId: string, context: AuditContext): Promise<ManualOrder> {
    // Get current order
    const order = await this.getOrderById(orderId, context.organizationId);

    if (order.status !== 'draft') {
      throw new Error('Can only confirm draft orders');
    }

    const oldStatus = order.status;

    // Create stock reservations
    await this.createStockReservations(orderId, context);

    // Update order status
    const { error } = await supabase
      .from('orders')
      .update({
        status: 'confirmed',
        confirmed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('organization_id', context.organizationId);

    if (error) {
      console.error('Error confirming order:', error);
      throw new Error(`Failed to confirm order: ${error.message}`);
    }

    // Log status change
    await useOrderStatusHistoryStore.getState().logStatusChange({
      orderId,
      channel: 'manual',
      orderNumber: order.order_number,
      fromStatus: oldStatus,
      toStatus: 'confirmed',
      source: 'manual'
    });

    return this.getOrderById(orderId, context.organizationId);
  }

  /**
   * Cancel an order
   * Releases all stock reservations
   */
  static async cancelOrder(
    orderId: string,
    reason: string,
    context: AuditContext
  ): Promise<ManualOrder> {
    // Get current order
    const order = await this.getOrderById(orderId, context.organizationId);

    if (['completed', 'cancelled'].includes(order.status)) {
      throw new Error('Cannot cancel a completed or already cancelled order');
    }

    const oldStatus = order.status;

    // Release stock reservations if order was confirmed
    if (order.status !== 'draft') {
      await this.releaseStockReservations(orderId, reason, context);
    }

    // Update order status
    const { error } = await supabase
      .from('orders')
      .update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancel_reason: reason,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('organization_id', context.organizationId);

    if (error) {
      console.error('Error cancelling order:', error);
      throw new Error(`Failed to cancel order: ${error.message}`);
    }

    // Log status change
    await useOrderStatusHistoryStore.getState().logStatusChange({
      orderId,
      channel: 'manual',
      orderNumber: order.order_number,
      fromStatus: oldStatus,
      toStatus: 'cancelled',
      source: 'manual',
      reason
    });

    return this.getOrderById(orderId, context.organizationId);
  }

  /**
   * Update order status
   */
  static async updateOrderStatus(
    orderId: string,
    newStatus: ManualOrderStatus,
    context: AuditContext,
    reason?: string
  ): Promise<ManualOrder> {
    // Get current order for old status
    const { data: currentOrder, error: fetchError } = await supabase
      .from('orders')
      .select('status, order_number')
      .eq('id', orderId)
      .eq('organization_id', context.organizationId)
      .single();

    if (fetchError) {
      throw new Error(`Order not found: ${fetchError.message}`);
    }

    const oldStatus = currentOrder.status;

    const updates: Record<string, unknown> = {
      status: newStatus,
      updated_at: new Date().toISOString()
    };

    // Set timestamp based on status
    if (newStatus === 'shipped') {
      updates.shipped_at = new Date().toISOString();
    } else if (newStatus === 'completed') {
      updates.completed_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', orderId)
      .eq('organization_id', context.organizationId);

    if (error) {
      console.error('Error updating order status:', error);
      throw new Error(`Failed to update order status: ${error.message}`);
    }

    // Log status change
    await useOrderStatusHistoryStore.getState().logStatusChange({
      orderId,
      channel: 'manual',
      orderNumber: currentOrder.order_number,
      fromStatus: oldStatus,
      toStatus: newStatus,
      source: 'manual',
      reason
    });

    return this.getOrderById(orderId, context.organizationId);
  }

  // ========================================
  // Stock Reservation Management
  // ========================================

  /**
   * Create stock reservations for all order items
   */
  static async createStockReservations(
    orderId: string,
    context: AuditContext
  ): Promise<void> {
    // Get order items
    const { data: items, error: itemsError } = await supabase
      .from('order_items')
      .select('id, product_id, quantity, product:products!inner(manage_stock,organization_id)')
      .eq('order_id', orderId);

    if (itemsError || !items) {
      throw new Error('Failed to fetch order items');
    }

    // Get order for reference
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('order_number')
      .eq('id', orderId)
      .single();

    if (orderError) {
      throw new Error('Failed to fetch order');
    }

    // Create reservations for each item
    for (const item of items) {
      const product = Array.isArray(item.product) ? item.product[0] : item.product;
      if (product?.organization_id !== context.organizationId) throw new Error('Product organization mismatch');
      if (product?.manage_stock === false) continue;
      try {
        // Find a zone with available stock for this product
        const { data: zoneStock } = await supabase
          .from('product_zone_storage')
          .select('zone_id, quantity')
          .eq('product_id', item.product_id)
          .gt('quantity', 0)
          .order('quantity', { ascending: false })
          .limit(1);

        const zoneId = zoneStock?.[0]?.zone_id;

        if (!zoneId) {
          console.warn(`No zone found with stock for product ${item.product_id}`);
          continue;
        }

        const reservation = await StockReservationService.createReservation(
          {
            product_id: item.product_id,
            zone_id: zoneId,
            quantity: item.quantity,
            reservation_type: 'order',
            reference_type: 'manual_order',
            reference_id: orderId,
            notes: `Ordine ${order.order_number}`,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
          },
          context
        );

        // Update order item with reservation ID
        await supabase
          .from('order_items')
          .update({ reservation_id: reservation.id })
          .eq('id', item.id);

      } catch (error) {
        console.error(`Failed to create reservation for item ${item.id}:`, error);
        // Continue with other items
      }
    }
  }

  /**
   * Release stock reservations for an order
   */
  static async releaseStockReservations(
    orderId: string,
    reason: string,
    context: AuditContext
  ): Promise<void> {
    // Get order items with reservations
    const { data: items, error } = await supabase
      .from('order_items')
      .select('reservation_id')
      .eq('order_id', orderId)
      .not('reservation_id', 'is', null);

    if (error || !items) {
      console.error('Failed to fetch order items:', error);
      return;
    }

    // Cancel each reservation
    for (const item of items) {
      if (item.reservation_id) {
        try {
          await StockReservationService.cancelReservation(
            item.reservation_id,
            reason,
            context
          );
        } catch (error) {
          console.error(`Failed to cancel reservation ${item.reservation_id}:`, error);
        }
      }
    }
  }

  /**
   * Fulfill stock reservations (when order preparation is complete)
   */
  static async fulfillStockReservations(
    orderId: string,
    context: AuditContext
  ): Promise<void> {
    // Get order items with reservations
    const { data: items, error } = await supabase
      .from('order_items')
      .select('reservation_id, quantity')
      .eq('order_id', orderId)
      .not('reservation_id', 'is', null);

    if (error || !items) {
      console.error('Failed to fetch order items:', error);
      return;
    }

    // Fulfill each reservation
    for (const item of items) {
      if (item.reservation_id) {
        try {
          await StockReservationService.fulfillReservation(
            item.reservation_id,
            item.quantity,
            context
          );
        } catch (error) {
          console.error(`Failed to fulfill reservation ${item.reservation_id}:`, error);
        }
      }
    }
  }

  // ========================================
  // Availability Check
  // ========================================

  /**
   * Check product availability for order items
   */
  static async checkItemsAvailability(
    items: OrderItemInput[],
    organizationId: string
  ): Promise<ProductAvailability[]> {
    const results: ProductAvailability[] = [];

    // Prodotti senza gestione stock: sempre disponibili, salta il check RPC
    const { data: stockFlags } = await supabase
      .from('products')
      .select('id, manage_stock')
      .eq('organization_id', organizationId)
      .in('id', items.map(i => i.product_id));

    const unmanagedIds = new Set(
      (stockFlags || []).filter(p => p.manage_stock === false).map(p => p.id)
    );

    for (const item of items) {
      if (unmanagedIds.has(item.product_id)) {
        results.push({
          product_id: item.product_id,
          product_name: item.product_name,
          stock_quantity: 0,
          reserved_quantity: 0,
          available_quantity: item.quantity,
          requested_quantity: item.quantity,
          is_available: true
        });
        continue;
      }
      // Use RPC to get available stock
      const { data, error } = await supabase.rpc('get_product_available_stock', {
        p_product_id: item.product_id
      });

      if (error) {
        console.error(`Error checking availability for ${item.product_id}:`, error);
        results.push({
          product_id: item.product_id,
          product_name: item.product_name,
          stock_quantity: 0,
          reserved_quantity: 0,
          available_quantity: 0,
          requested_quantity: item.quantity,
          is_available: false,
          shortage: item.quantity
        });
        continue;
      }

      const stockInfo = data?.[0] || { stock_quantity: 0, reserved_quantity: 0, available_quantity: 0 };
      const isAvailable = stockInfo.available_quantity >= item.quantity;

      results.push({
        product_id: item.product_id,
        product_name: item.product_name,
        stock_quantity: stockInfo.stock_quantity,
        reserved_quantity: stockInfo.reserved_quantity,
        available_quantity: stockInfo.available_quantity,
        requested_quantity: item.quantity,
        is_available: isAvailable,
        shortage: isAvailable ? undefined : item.quantity - stockInfo.available_quantity
      });
    }

    return results;
  }

  // ========================================
  // Duplicate Order
  // ========================================

  /**
   * Duplicate an existing order (for quick reorder)
   */
  static async duplicateOrder(
    sourceOrderId: string,
    context: AuditContext
  ): Promise<ManualOrder> {
    // Get source order
    const sourceOrder = await this.getOrderById(sourceOrderId, context.organizationId);

    // Convert items to input format
    const items: OrderItemInput[] = (sourceOrder.items || []).map(item => ({
      product_id: item.product_id,
      variation_id: item.variation_id,
      product_name: item.product_name,
      product_sku: item.product_sku,
      sale_unit: item.sale_unit ?? null,
      pack_weight_kg: item.pack_weight_kg ?? null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_amount: item.discount_amount,
      tax_rate: item.tax_rate,
      notes: item.notes,
      line_type: item.line_type === 'custom' ? 'custom' : undefined
    }));

    // Create new order
    // NB: deal_id NON viene copiato. Un riordino e' un ordine nuovo, non una
    // seconda evasione della stessa offerta: le righe di un'offerta si dividono
    // tra ordini solo da «Dividi in ordini» (flusia-core controlla i residui), e un
    // duplicato collegato all'offerta la farebbe risultare ordinata due volte.
    return this.createOrder({
      client_id: sourceOrder.client_id,
      price_list_id: sourceOrder.price_list_id,
      billing_address: sourceOrder.billing_address,
      shipping_address: sourceOrder.shipping_address,
      shipping_method: sourceOrder.shipping_method,
      shipping_cost: sourceOrder.shipping_cost,
      items,
      auto_confirm: false // Start as draft
    }, context);
  }

  // ========================================
  // Order Items Management
  // ========================================

  /**
   * Add item to order (only for draft orders)
   */
  static async addOrderItem(
    orderId: string,
    item: OrderItemInput,
    context: AuditContext
  ): Promise<OrderItem> {
    // Check order status
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('status')
      .eq('id', orderId)
      .eq('organization_id', context.organizationId)
      .single();

    if (orderError || order?.status !== 'draft') {
      throw new Error('Can only add items to draft orders');
    }

    // Get max sort_order
    const { data: maxSort } = await supabase
      .from('order_items')
      .select('sort_order')
      .eq('order_id', orderId)
      .order('sort_order', { ascending: false })
      .limit(1);

    const sortOrder = (maxSort?.[0]?.sort_order || 0) + 1;
    const lineTotal = (item.unit_price * item.quantity) - (item.discount_amount || 0);

    // Insert item
    const { data: newItem, error } = await supabase
      .from('order_items')
      .insert({
        order_id: orderId,
        product_id: item.product_id,
        variation_id: item.variation_id,
        product_name: item.product_name,
        product_sku: item.product_sku,
      sale_unit: item.sale_unit ?? null,
      pack_weight_kg: item.pack_weight_kg ?? null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_amount: item.discount_amount || 0,
        line_total: lineTotal,
        tax_rate: item.tax_rate || 0,
        sort_order: sortOrder,
        notes: item.notes,
        line_type: item.line_type ?? 'product'
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to add item: ${error.message}`);
    }

    // Recalculate order totals
    await this.recalculateOrderTotals(orderId);

    return newItem as OrderItem;
  }

  /**
   * Remove item from order (only for draft orders)
   */
  static async removeOrderItem(
    orderId: string,
    itemId: string,
    context: AuditContext
  ): Promise<void> {
    // Check order status
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('status')
      .eq('id', orderId)
      .eq('organization_id', context.organizationId)
      .single();

    if (orderError || order?.status !== 'draft') {
      throw new Error('Can only remove items from draft orders');
    }

    // Delete item
    const { error } = await supabase
      .from('order_items')
      .delete()
      .eq('id', itemId)
      .eq('order_id', orderId);

    if (error) {
      throw new Error(`Failed to remove item: ${error.message}`);
    }

    // Recalculate order totals
    await this.recalculateOrderTotals(orderId);
  }

  /**
   * Recalculate order totals based on items
   */
  static async recalculateOrderTotals(orderId: string): Promise<void> {
    // Get all items
    const { data: items, error } = await supabase
      .from('order_items')
      .select('line_total, tax_rate')
      .eq('order_id', orderId);

    if (error || !items) {
      console.error('Failed to fetch items for recalculation:', error);
      return;
    }

    const subtotal = items.reduce((sum, item) => sum + Number(item.line_total), 0);
    const taxAmount = items.reduce((sum, item) => {
      return sum + (Number(item.line_total) * Number(item.tax_rate) / 100);
    }, 0);

    // Get current shipping cost
    const { data: order } = await supabase
      .from('orders')
      .select('shipping_cost')
      .eq('id', orderId)
      .single();

    const shippingCost = order?.shipping_cost || 0;
    const total = subtotal + taxAmount + shippingCost;

    // Update order totals
    await supabase
      .from('orders')
      .update({
        subtotal,
        tax_amount: taxAmount,
        total,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);
  }
}
